import React from 'react';

import { MOCK_LATENCY } from '../theme/tokens';

export type MockEndpoint<T> = {
  /**
   * The endpoint's value. Present immediately (the mock data is local) — gate its
   * reveal on `loading`, don't treat it as absent. When a real API replaces this,
   * the same field carries the fetched payload (and may be `undefined` on the very
   * first load, which the caller handles where it maps over it).
   */
  data: T;
  /** True until the simulated endpoint latency elapses, then false for good. */
  loading: boolean;
};

export type UseMockEndpointOptions = {
  /**
   * Share ONE simulated fetch across every caller that passes the same id, so a
   * group of figures sourced from the same endpoint flip loading→loaded together
   * (one wait for many KPIs) even when they live in different, unrelated components.
   *
   * Omit it and each call gets its own independent latency. That is the usual page
   * pattern: call `useMockEndpoint` once per logical endpoint (≈ per section) and
   * pass `loading` down — every figure in that section then shares the one wait,
   * while separate sections fill in on their own randomised delays.
   */
  groupId?: string;
};

/**
 * Randomised stand-in for network latency: most endpoints land quickly (the common
 * band), a minority straggle (the outlier band) — so a page fills in unevenly the
 * way real endpoints do instead of every number snapping in at once.
 */
const randomLatencyMs = (): number => {
  const { MIN_MS, MAX_MS, OUTLIER_MIN_MS, OUTLIER_MAX_MS, OUTLIER_CHANCE } = MOCK_LATENCY;
  const [lo, hi] = Math.random() < OUTLIER_CHANCE ? [OUTLIER_MIN_MS, OUTLIER_MAX_MS] : [MIN_MS, MAX_MS];
  return lo + Math.random() * (hi - lo);
};

type Entry = {
  loading: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  refs: number;
  listeners: Set<() => void>;
};

// One entry per endpoint key, created on first subscribe (which kicks off the
// latency timer) and dropped when its last subscriber unmounts — so navigating
// away and back re-fetches (the chosen "skeletons replay on every visit"
// behaviour). A real data cache would keep resolved entries around instead; that
// swap lives entirely behind this hook's signature.
const registry = new Map<string, Entry>();

const ensureEntry = (key: string): Entry => {
  const existing = registry.get(key);
  if (existing) return existing;

  const entry: Entry = { loading: true, timer: null, refs: 0, listeners: new Set() };
  registry.set(key, entry);
  entry.timer = setTimeout(() => {
    entry.loading = false;
    entry.timer = null;
    entry.listeners.forEach((notify) => notify());
  }, randomLatencyMs());
  return entry;
};

const subscribe = (key: string, onChange: () => void): (() => void) => {
  const entry = ensureEntry(key);
  entry.refs += 1;
  entry.listeners.add(onChange);
  return () => {
    entry.listeners.delete(onChange);
    entry.refs -= 1;
    if (entry.refs <= 0) {
      if (entry.timer) clearTimeout(entry.timer);
      registry.delete(key);
    }
  };
};

// Before the first subscribe runs (during the initial render) there is no entry
// yet, so default to loading — the figure shows its skeleton on first paint.
const isLoading = (key: string): boolean => registry.get(key)?.loading ?? true;

/**
 * Simulate loading a data point (or a group of them) from an endpoint that does
 * not exist yet. Returns `{ data, loading }`: `loading` is true until a randomised
 * latency elapses, at which point the figure should reveal. The presentational
 * layer only ever sees this `{ data, loading }` shape, so swapping in a real
 * source later — `const { data, loading } = useThingQuery()` — needs no change to
 * the components that consume it.
 *
 * Grouping: pass the same `groupId` from several callers to make them share one
 * wait (see {@link UseMockEndpointOptions.groupId}); omit it for an independent
 * per-call delay (the common page pattern — one call per section).
 */
export const useMockEndpoint = <T>(value: T, options?: UseMockEndpointOptions): MockEndpoint<T> => {
  // A stable per-instance id backs the ungrouped case so it flows through the same
  // shared registry as the grouped one (one code path), keyed uniquely per call.
  const autoId = React.useId();
  const key = options?.groupId ?? autoId;

  const subscribeToKey = React.useCallback((onChange: () => void) => subscribe(key, onChange), [key]);
  const getSnapshot = React.useCallback(() => isLoading(key), [key]);

  const loading = React.useSyncExternalStore(subscribeToKey, getSnapshot, getSnapshot);

  return { data: value, loading };
};

export default useMockEndpoint;
